/**
 * Responsive Images JavaScript
 * 
 * Handles lazy loading, WebP detection, and image optimization
 */

class ResponsiveImages {
  constructor() {
    this.init();
  }

  init() {
    this.setupLazyLoading();
    this.setupImageErrorHandling();
    this.setupWebPDetection();
  }

  /**
   * Set up Intersection Observer for lazy loading
   */
  setupLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            this.loadImage(img);
            observer.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      // Observe all lazy images
      document.querySelectorAll('img[loading="lazy"]').forEach(img => {
        imageObserver.observe(img);
      });
    } else {
      // Fallback for browsers without Intersection Observer
      this.loadAllImages();
    }
  }

  /**
   * Load individual image
   */
  loadImage(img) {
    if (img.dataset.src) {
      img.src = img.dataset.src;
    }
    
    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
    }

    img.addEventListener('load', () => {
      img.classList.add('loaded');
    });

    img.addEventListener('error', () => {
      this.handleImageError(img);
    });
  }

  /**
   * Load all images (fallback)
   */
  loadAllImages() {
    document.querySelectorAll('img[loading="lazy"]').forEach(img => {
      this.loadImage(img);
    });
  }

  /**
   * Handle image loading errors
   */
  setupImageErrorHandling() {
    document.addEventListener('error', (e) => {
      if (e.target.tagName === 'IMG') {
        this.handleImageError(e.target);
      }
    }, true);
  }

  /**
   * Handle individual image error
   */
  handleImageError(img) {
    console.warn('Image failed to load:', img.src);
    
    // Try fallback format if WebP failed
    if (img.src.includes('.webp')) {
      const fallbackSrc = img.src.replace('.webp', '.jpg').replace('.webp', '.png');
      img.src = fallbackSrc;
      return;
    }

    // Add error class for styling
    img.classList.add('image-error');
    
    // Optional: Replace with placeholder
    if (img.dataset.placeholder) {
      img.src = img.dataset.placeholder;
    }
  }

  /**
   * Enhanced WebP detection (backup to CSS method)
   */
  setupWebPDetection() {
    // This is a backup to the CSS method in case JS loads first
    if (!document.documentElement.classList.contains('webp') && 
        !document.documentElement.classList.contains('no-webp')) {
      
      const webP = new Image();
      webP.onload = webP.onerror = function () {
        const hasWebP = webP.height === 2;
        document.documentElement.classList.add(hasWebP ? 'webp' : 'no-webp');
      };
      webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    }
  }

  /**
   * Preload critical images
   */
  preloadCriticalImages() {
    const criticalImages = document.querySelectorAll('[data-preload="true"]');
    
    criticalImages.forEach(img => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = img.src || img.dataset.src;
      
      if (img.srcset || img.dataset.srcset) {
        link.imagesrcset = img.srcset || img.dataset.srcset;
        link.imagesizes = img.sizes || '100vw';
      }
      
      document.head.appendChild(link);
    });
  }

  /**
   * Update image sizes based on container
   */
  updateImageSizes() {
    const responsiveImages = document.querySelectorAll('.responsive-image__img[data-auto-sizes="true"]');
    
    responsiveImages.forEach(img => {
      const container = img.closest('.responsive-image');
      if (container) {
        const containerWidth = container.offsetWidth;
        img.sizes = `${containerWidth}px`;
      }
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.takumoResponsiveImages = new ResponsiveImages();
});

// Update sizes on resize
window.addEventListener('resize', () => {
  if (window.takumoResponsiveImages) {
    window.takumoResponsiveImages.updateImageSizes();
  }
});

// Export for use in other modules
export default ResponsiveImages;